import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button, EmptyState, Icon, IconButton, Modal, PageBody, Pill, SegmentedControl, Skeleton, Textarea, useToast } from "@astrum/ui";
import { addDays, buildGrid, formatClock, isDateKey, weekDays, weekStartOf } from "@astrum/shared";
import type { TimeEntry, Timesheet as TimesheetData } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { ExportMenu } from "../components/ExportMenu.js";
import { LogTimeModal } from "../components/LogTimeModal.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import { dayHeading, isEditableStatus, liveMinutes, shiftWeek, STATUS_LABEL, STATUS_TONE, weekLabel } from "../lib/time.js";
import "../components/log-time-modal.css";
import "./timesheet.css";

/**
 * The weekly timesheet: a task × day grid of the week's time, with submit for
 * the owner and, for approvers, a queue to approve or send back. Looking at
 * someone else's week (from the queue) is read-only.
 */

const todayKey = () => new Date().toLocaleDateString("en-CA");

type Tab = "mine" | "approvals";
type ApprovalFilter = "submitted" | "approved" | "rejected";

const FILTER_OPTIONS: { value: ApprovalFilter; label: string }[] = [
  { value: "submitted", label: "Waiting" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Sent back" },
];

export function Timesheet() {
  usePageTitle("Timesheet");
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const today = todayKey();
  const tab: Tab = params.get("tab") === "approvals" ? "approvals" : "mine";
  const weekParam = params.get("week");
  const weekStart = weekStartOf(weekParam && isDateKey(weekParam) ? weekParam : today);
  const subject = params.get("user") ?? undefined;

  const [approver, setApprover] = useState(false);
  useEffect(() => {
    api
      .get<{ approver: boolean }>("/time/approvals?limit=1")
      .then((res) => setApprover(res.approver))
      .catch(() => setApprover(false));
  }, []);

  const update = (next: Record<string, string | undefined>) => {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined) merged.delete(key);
      else merged.set(key, value);
    }
    setParams(merged, { replace: true });
  };

  const showApprovals = tab === "approvals" && approver && !subject;

  return (
    <PageBody
      title="Timesheet"
      subtitle="What you worked on, week by week."
      width="wide"
      actions={
        approver && !subject ? (
          <SegmentedControl
            aria-label="Timesheet view"
            value={tab}
            onChange={(value) => update({ tab: value === "mine" ? undefined : value })}
            options={[
              { value: "mine", label: "My timesheet" },
              { value: "approvals", label: "Approvals" },
            ]}
          />
        ) : undefined
      }
    >
      {showApprovals ? (
        <Approvals
          onOpen={(sheet) => update({ tab: undefined, user: sheet.userId, week: sheet.weekStart })}
          toast={(message) => toast(message, { tone: "danger" })}
        />
      ) : (
        <Week
          key={`${subject ?? "me"}-${weekStart}`}
          weekStart={weekStart}
          subject={subject}
          today={today}
          onWeek={(week) => update({ week: week === weekStartOf(today) ? undefined : week })}
          onBack={() => update({ user: undefined, week: undefined, tab: "approvals" })}
        />
      )}
    </PageBody>
  );
}

/* ───────────────────────────────── a week ───────────────────────────────── */

interface WeekProps {
  weekStart: string;
  /** Someone else's id: the week is shown read-only. */
  subject?: string;
  today: string;
  onWeek: (week: string) => void;
  onBack: () => void;
}

function Week({ weekStart, subject, today, onWeek, onBack }: WeekProps) {
  const { toast } = useToast();
  const [sheet, setSheet] = useState<TimesheetData | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntry | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ week: weekStart });
      if (subject) qs.set("userId", subject);
      const res = await api.get<{ timesheet: TimesheetData; entries: TimeEntry[] }>(`/time/timesheet?${qs.toString()}`);
      setSheet(res.timesheet);
      setEntries(res.entries);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Could not load the timesheet.", { tone: "danger" });
    } finally {
      setLoading(false);
    }
  }, [weekStart, subject, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const hasRunning = entries.some((e) => e.running);
  useEffect(() => {
    if (!hasRunning) return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [hasRunning]);

  const grid = useMemo(
    () => buildGrid(entries.map((e) => ({ taskId: e.taskId, taskTitle: e.taskTitle, projectCode: e.projectCode, date: e.date, minutes: liveMinutes(e, now) })), weekStart),
    [entries, now, weekStart],
  );

  const readOnly = subject !== undefined;
  const editable = !readOnly && sheet !== null && isEditableStatus(sheet.status);
  const isCurrent = weekStart === weekStartOf(today);
  const canSubmit = editable && grid.total > 0 && !hasRunning && weekStart <= today;
  const days = weekDays(weekStart);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/time/timesheet/submit", { week: weekStart });
      toast("Timesheet submitted.");
      await load();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Could not submit.", { tone: "danger" });
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (entry: TimeEntry) => {
    try {
      await api.delete(`/time/entries/${entry.id}`);
      await load();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Could not delete that entry.", { tone: "danger" });
    }
  };

  return (
    <>
      <div className="timesheet__bar">
        {readOnly && (
          <Button variant="ghost" size="sm" icon={<Icon name="chevron-left" size={14} />} onClick={onBack}>
            Approvals
          </Button>
        )}
        <div className="timesheet__nav" role="group" aria-label="Week">
          <IconButton icon={<Icon name="chevron-left" size={16} />} label="Previous week" onClick={() => onWeek(shiftWeek(weekStart, -1))} />
          <span className="timesheet__week" aria-live="polite">
            {weekLabel(weekStart)}
          </span>
          <IconButton icon={<Icon name="chevron-right" size={16} />} label="Next week" onClick={() => onWeek(shiftWeek(weekStart, 1))} />
          {!isCurrent && (
            <Button variant="ghost" size="sm" onClick={() => onWeek(weekStartOf(today))}>
              This week
            </Button>
          )}
        </div>
        <div className="timesheet__tools">
          {sheet && <Pill tone={STATUS_TONE[sheet.status]}>{STATUS_LABEL[sheet.status]}</Pill>}
          {!readOnly && <ExportMenu excelPath={`/exports/time.xlsx?from=${weekStart}&to=${addDays(weekStart, 6)}`} kind="timesheet" />}
          {editable && (
            <Button variant="secondary" size="sm" icon={<Icon name="plus" size={14} />} onClick={() => setModalOpen(true)}>
              Add time
            </Button>
          )}
          {editable && (
            <Button size="sm" onClick={() => void submit()} loading={submitting} disabled={!canSubmit}>
              Submit week
            </Button>
          )}
        </div>
      </div>

      {sheet && readOnly && <p className="timesheet__note">{sheet.userName}</p>}
      {sheet?.status === "rejected" && sheet.decisionNote && (
        <p className="timesheet__note timesheet__note--warn" role="status">
          Sent back{sheet.decidedByName ? ` by ${sheet.decidedByName}` : ""}: {sheet.decisionNote}
        </p>
      )}
      {sheet?.status === "approved" && (
        <p className="timesheet__note" role="status">
          Approved{sheet.decidedByName ? ` by ${sheet.decidedByName}` : ""}.
        </p>
      )}
      {editable && hasRunning && <p className="timesheet__note">Stop your running timer before submitting this week.</p>}

      {loading ? (
        <Skeleton height={220} />
      ) : grid.rows.length === 0 ? (
        <EmptyState
          title="No time this week"
          description={editable ? "Start a timer on a task, or add time by hand." : "Nothing was logged for this week."}
        />
      ) : (
        <>
          <div className="timesheet__scroll">
            <table className="timesheet__grid">
              <thead>
                <tr>
                  <th scope="col">Task</th>
                  {days.map((d, i) => (
                    <th key={d} scope="col" className={d === today ? "is-today" : undefined}>
                      {dayHeading(weekStart, i)}
                    </th>
                  ))}
                  <th scope="col">Total</th>
                </tr>
              </thead>
              <tbody>
                {grid.rows.map((row) => (
                  <tr key={row.taskId ?? `purged:${row.taskTitle ?? ""}`}>
                    <th scope="row" className="timesheet__task">
                      {row.projectCode && <span className="timesheet__code">{row.projectCode}</span>}
                      {row.taskId ? (
                        <Link to={`/work/task/${row.taskId}`}>{row.taskTitle ?? "Task"}</Link>
                      ) : (
                        <span title="This task has been deleted">{row.taskTitle ?? "Deleted task"}</span>
                      )}
                    </th>
                    {row.days.map((m, i) => (
                      <td key={i}>{m > 0 ? formatClock(m) : ""}</td>
                    ))}
                    <td className="timesheet__sum">{formatClock(row.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  {grid.dayTotals.map((m, i) => (
                    <td key={i}>{m > 0 ? formatClock(m) : ""}</td>
                  ))}
                  <td className="timesheet__sum">{formatClock(grid.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <h2 className="timesheet__heading">Entries</h2>
          <ul className="timesheet__entries">
            {entries.map((entry) => (
              <li key={entry.id} className="timesheet__entry">
                <span className="timesheet__entry-day">{entry.date}</span>
                {entry.taskId ? (
                  <Link to={`/work/task/${entry.taskId}`} className="timesheet__entry-task">
                    {entry.taskTitle}
                  </Link>
                ) : (
                  <span className="timesheet__entry-task" title="This task has been deleted">
                    {entry.taskTitle ?? "Deleted task"}
                  </span>
                )}
                <span className="timesheet__entry-minutes">{entry.running ? "Running" : formatClock(entry.minutes)}</span>
                <span className="timesheet__entry-note">{entry.note}</span>
                {editable && !entry.locked && !entry.running && (
                  <span className="timesheet__entry-actions">
                    <IconButton icon={<Icon name="edit" size={14} />} label="Edit entry" size="sm" onClick={() => setEditing(entry)} />
                    <IconButton icon={<Icon name="trash" size={14} />} label="Delete entry" size="sm" onClick={() => void remove(entry)} />
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      <LogTimeModal
        open={modalOpen || editing !== undefined}
        onClose={() => {
          setModalOpen(false);
          setEditing(undefined);
        }}
        onSaved={() => void load()}
        today={today}
        defaultDate={weekStart <= today && today <= addDays(weekStart, 6) ? today : weekStart}
        entry={editing}
      />
    </>
  );
}

/* ─────────────────────────────── approvals ─────────────────────────────── */

type Sheet = TimesheetData & { id: string };

function Approvals({ onOpen, toast }: { onOpen: (sheet: Sheet) => void; toast: (message: string) => void }) {
  const { toast: notify } = useToast();
  const [status, setStatus] = useState<ApprovalFilter>("submitted");
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState<Sheet | null>(null);
  const [note, setNote] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(
    async (after?: string) => {
      try {
        const qs = new URLSearchParams({ status, limit: "50" });
        if (after) qs.set("cursor", after);
        const res = await api.get<{ timesheets: Sheet[]; nextCursor: string | null }>(`/time/approvals?${qs.toString()}`);
        setSheets((list) => (after ? [...list, ...res.timesheets] : res.timesheets));
        setCursor(res.nextCursor);
      } catch (e) {
        toast(e instanceof ApiError ? e.message : "Could not load approvals.");
      } finally {
        setLoading(false);
      }
    },
    [status, toast],
  );

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const decide = async (sheet: Sheet, decision: "approve" | "reject", text?: string) => {
    setBusyId(sheet.id);
    try {
      await api.post(`/time/timesheets/${sheet.id}/${decision}`, text ? { note: text } : {});
      notify(decision === "approve" ? "Timesheet approved." : "Timesheet sent back.");
      setSheets((list) => list.filter((s) => s.id !== sheet.id));
      setRejecting(null);
      setNote("");
    } catch (e) {
      notify(e instanceof ApiError ? e.message : "Could not save that decision.", { tone: "danger" });
    } finally {
      setBusyId(null);
    }
  };

  const range = (() => {
    const today = todayKey();
    const from = addDays(weekStartOf(today), -7 * 12);
    return `from=${from}&to=${addDays(weekStartOf(today), 6)}`;
  })();

  return (
    <>
      <div className="timesheet__bar">
        <SegmentedControl aria-label="Status" value={status} onChange={setStatus} options={FILTER_OPTIONS} />
        <div className="timesheet__tools">
          <ExportMenu excelPath={`/exports/time.xlsx?scope=team&${range}`} kind="team time" />
        </div>
      </div>

      {loading ? (
        <Skeleton height={160} />
      ) : sheets.length === 0 ? (
        <EmptyState title="Nothing here" description={status === "submitted" ? "No timesheets are waiting for you." : "No timesheets match."} />
      ) : (
        <ul className="timesheet__queue">
          {sheets.map((sheet) => (
            <li key={sheet.id} className="timesheet__queue-row">
              <div className="timesheet__queue-who">
                <strong>{sheet.userName}</strong>
                <span>{weekLabel(sheet.weekStart)}</span>
              </div>
              <span className="timesheet__queue-total">{formatClock(sheet.totalMinutes)}</span>
              <Pill tone={STATUS_TONE[sheet.status]}>{STATUS_LABEL[sheet.status]}</Pill>
              <div className="timesheet__queue-actions">
                <Button variant="ghost" size="sm" onClick={() => onOpen(sheet)}>
                  View
                </Button>
                {sheet.status === "submitted" && (
                  <>
                    <Button variant="secondary" size="sm" onClick={() => setRejecting(sheet)} disabled={busyId === sheet.id}>
                      Send back
                    </Button>
                    <Button size="sm" onClick={() => void decide(sheet, "approve")} loading={busyId === sheet.id}>
                      Approve
                    </Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {cursor && (
        <Button variant="ghost" size="sm" onClick={() => void load(cursor)}>
          Show more
        </Button>
      )}

      <Modal open={rejecting !== null} onClose={() => setRejecting(null)} title="Send timesheet back">
        <form
          className="log-time"
          onSubmit={(e) => {
            e.preventDefault();
            if (rejecting && note.trim()) void decide(rejecting, "reject", note.trim());
          }}
        >
          <Textarea label="What needs fixing?" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} autoFocus />
          <div className="log-time__actions">
            <Button type="button" variant="ghost" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!note.trim()} loading={busyId !== null}>
              Send back
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
